import { useCallback } from 'react';

import { useUser } from '@clerk/react';
import log from 'loglevel';

import { useGetPaginatedTaskGroups } from '../../services/taskGroups.service';
import { useInboxEntriesStore } from '../../store';
import { useFetchCacheStore } from '../../store/FetchCache';
import { useActiveTeamId, useTeamStore } from '../../store/Team/useTeamStore';
import { createRequestGuard } from '../requestGuard';

const FETCH_TASK_GROUPS_LIMIT = 50;

interface FetchTaskGroupsParams {
  limit?: number;
}

const getActiveTeamId = (): string | undefined => {
  const { activeTeamId, userTeamIds } = useTeamStore.getState();
  return activeTeamId ?? userTeamIds?.[0] ?? undefined;
};

const taskGroupsRequestGuard = createRequestGuard(getActiveTeamId);

export const useFetchTaskGroups = ({
  limit = FETCH_TASK_GROUPS_LIMIT,
}: FetchTaskGroupsParams = {}) => {
  const { getPaginatedTaskGroups } = useGetPaginatedTaskGroups();
  const { user } = useUser();
  const activeTeamId = useActiveTeamId();
  const { saveTaskGroups, setTaskGroupsLoading, exclusiveStartKey } =
    useInboxEntriesStore((state) => state.taskManager.taskGroups);

  const defaultTaskGroupId =
    (user?.unsafeMetadata?.defaultTaskGroupId as string) ?? null;

  const handleFetchTaskGroups = useCallback(
    async (forceRefresh = false) => {
      // Wait for the active team — fetching without it returns every team's
      // task groups instead of the scoped set.
      if (!activeTeamId) {
        return;
      }

      const { isTaskGroupsLoaded, markTaskGroupsAsLoaded } =
        useFetchCacheStore.getState();

      if (!forceRefresh && isTaskGroupsLoaded(activeTeamId)) return;

      if (taskGroupsRequestGuard.isStaleRequest(activeTeamId)) {
        return;
      }
      const requestId = taskGroupsRequestGuard.startRequest();

      setTaskGroupsLoading(true);
      try {
        const { taskGroups, exclusiveStartKey: newExclusiveStartKey } =
          await getPaginatedTaskGroups({
            limit,
            teamId: activeTeamId,
            includeDefaultTaskGroup: true,
          });
        if (
          taskGroupsRequestGuard.isStaleRequest(activeTeamId) ||
          taskGroupsRequestGuard.isSupersededRequest(requestId)
        ) {
          return;
        }
        saveTaskGroups(taskGroups, newExclusiveStartKey || null);
        markTaskGroupsAsLoaded(activeTeamId);
      } catch (error) {
        log.error('Error fetching task groups:', error);
      } finally {
        if (
          !taskGroupsRequestGuard.isStaleRequest(activeTeamId) &&
          !taskGroupsRequestGuard.isSupersededRequest(requestId)
        ) {
          setTaskGroupsLoading(false);
        }
      }
    },
    [
      getPaginatedTaskGroups,
      limit,
      activeTeamId,
      saveTaskGroups,
      setTaskGroupsLoading,
    ],
  );

  const refetchForActiveTeam = useCallback(async () => {
    // Same guard as handleFetchTaskGroups: fetching without a team returns
    // every team's groups instead of the scoped set.
    if (!activeTeamId) {
      return;
    }

    if (taskGroupsRequestGuard.isStaleRequest(activeTeamId)) {
      return;
    }
    const requestId = taskGroupsRequestGuard.startRequest();

    const cachedDefault = useInboxEntriesStore
      .getState()
      .taskManager.taskGroups.taskGroups.find(
        (tg) => tg.id === defaultTaskGroupId,
      );

    setTaskGroupsLoading(true);
    try {
      const { taskGroups, exclusiveStartKey: newExclusiveStartKey } =
        await getPaginatedTaskGroups({
          limit,
          teamId: activeTeamId,
          includeDefaultTaskGroup: !cachedDefault,
        });

      if (
        taskGroupsRequestGuard.isStaleRequest(activeTeamId) ||
        taskGroupsRequestGuard.isSupersededRequest(requestId)
      ) {
        return;
      }

      const merged =
        cachedDefault && !taskGroups.some((tg) => tg.id === cachedDefault.id)
          ? [cachedDefault, ...taskGroups]
          : taskGroups;

      saveTaskGroups(merged, newExclusiveStartKey || null);
      useFetchCacheStore.getState().markTaskGroupsAsLoaded(activeTeamId);
    } catch (error) {
      log.error('Error refetching task groups for active team:', error);
    } finally {
      if (
        !taskGroupsRequestGuard.isStaleRequest(activeTeamId) &&
        !taskGroupsRequestGuard.isSupersededRequest(requestId)
      ) {
        setTaskGroupsLoading(false);
      }
    }
  }, [
    getPaginatedTaskGroups,
    limit,
    activeTeamId,
    defaultTaskGroupId,
    saveTaskGroups,
    setTaskGroupsLoading,
  ]);

  const handleLoadMore = useCallback(async () => {
    if (!activeTeamId) {
      return;
    }

    if (!exclusiveStartKey) {
      return;
    }

    setTaskGroupsLoading(true);
    try {
      const { taskGroups, exclusiveStartKey: newExclusiveStartKey } =
        await getPaginatedTaskGroups({
          limit,
          exclusiveStartKey,
          teamId: activeTeamId,
          includeDefaultTaskGroup: false,
        });
      saveTaskGroups(taskGroups, newExclusiveStartKey || null, true);
    } catch (error) {
      log.error('Error loading more task groups:', error);
    } finally {
      setTaskGroupsLoading(false);
    }
  }, [
    getPaginatedTaskGroups,
    limit,
    exclusiveStartKey,
    activeTeamId,
    saveTaskGroups,
    setTaskGroupsLoading,
  ]);

  const hasMore = Boolean(exclusiveStartKey);

  return {
    handleFetchTaskGroups,
    refetchForActiveTeam,
    handleLoadMore,
    hasMore,
  };
};
